import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Anchor, Lock, User, Mail, LinkIcon, Building2, CheckCircle2, ShieldCheck, BarChart3, Globe } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';

const FEATURES = [
  { icon: ShieldCheck, text: 'RISQ 3.2 Standard — 588 compliance questions' },
  { icon: BarChart3,   text: 'Real-time fleet readiness scores & analytics' },
  { icon: Globe,       text: 'RightShip-aligned inspection framework' },
  { icon: CheckCircle2,text: 'Automated PDF & Excel audit reports' },
];

export const AuthPage = () => {
  const { token: inviteToken } = useParams();
  const [mode, setMode] = useState(inviteToken ? 'invite-register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCompany, setInviteCompany] = useState(null);
  const [inviteVessel, setInviteVessel] = useState(null);
  const [inviteRole, setInviteRole] = useState('VESSEL');
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (inviteToken) {
      api.validateInvite(inviteToken).then(res => {
        setInviteCompany(res.data.company_name);
        setInviteVessel(res.data.vessel_name);
        setInviteRole(res.data.role || 'VESSEL');
        setMode('invite-register');
      }).catch(() => {
        toast.error('Invalid or expired invite link');
        navigate('/auth');
      });
    }
  }, [inviteToken, navigate]);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const isRegister = mode === 'register-company' || mode === 'invite-register';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Login successful!');
      } else if (mode === 'invite-register') {
        await register(email, password, name, inviteToken || undefined);
        toast.success('Registration successful! You have been linked to your fleet.');
      } else {
        if (!companyName.trim()) {
          toast.error('Company name is required');
          setLoading(false);
          return;
        }
        await register(email, password, name, undefined, companyName.trim());
        toast.success('Registration successful! Welcome to your dashboard.');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ── Left panel ── */}
      <div
        className="hidden lg:flex lg:w-[52%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #001f5c 0%, #00144a 55%, #000b2e 100%)' }}
      >
        {/* subtle wave overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `radial-gradient(ellipse 80% 60% at 20% 110%, rgba(255,255,255,0.04) 0%, transparent 70%),
                            radial-gradient(ellipse 50% 40% at 80% -10%, rgba(255,255,255,0.06) 0%, transparent 60%)`,
        }} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
            <Anchor className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="text-white font-semibold text-sm tracking-wide">RISQ Master Hub</div>
            <div className="text-white/50 text-xs tracking-wider uppercase">Maritime Compliance Platform</div>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-bold text-white leading-tight" style={{ letterSpacing: '-0.03em' }}>
              Fleet compliance,<br />
              <span style={{ color: '#7eb8ff' }}>elevated.</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed max-w-sm">
              The maritime inspection platform built for companies that take RightShip compliance seriously.
            </p>
          </div>

          {/* Feature bullets */}
          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-white/80" strokeWidth={2} />
                </div>
                <span className="text-white/70 text-sm leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer credit */}
        <div className="relative z-10">
          <div className="h-px bg-white/10 mb-5" />
          <p className="text-white/30 text-xs tracking-wide">© 2026 RISQ Master Hub · Maritime Intelligence</p>
          <div className="flex gap-4 mt-2">
            <a href="https://www.bluehorizonsolutions.net/privacy.html" target="_blank" rel="noreferrer" className="text-white/30 text-xs hover:text-white/60 transition-colors">Privacy Policy</a>
            <a href="https://www.bluehorizonsolutions.net/terms.html" target="_blank" rel="noreferrer" className="text-white/30 text-xs hover:text-white/60 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex items-center justify-center bg-[#f8fafc] px-6 py-12 relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle className="bg-slate-100 hover:bg-slate-200 !text-slate-600" />
        </div>
        <div className="w-full max-w-[400px] space-y-8">

          {/* Mobile logo (hidden on lg) */}
          <div className="flex lg:hidden items-center gap-2 justify-center mb-2">
            <Anchor className="w-6 h-6 text-navy-700" strokeWidth={2} />
            <span className="text-navy-900 font-bold text-base tracking-tight">RISQ Master Hub</span>
          </div>

          {/* Form heading */}
          <div className="space-y-1.5">
            <h1 className="text-2xl font-bold text-slate-900" style={{ letterSpacing: '-0.02em' }} data-testid="auth-page-title">
              {mode === 'login' ? 'Sign in to your account' :
               mode === 'invite-register' && inviteRole === 'SUPERINTENDENT' ? 'Join as Superintendent' :
               mode === 'invite-register' ? 'Join your fleet' :
               'Register your company'}
            </h1>
            <p className="text-sm text-slate-500">
              {mode === 'login' ? 'Welcome back — enter your credentials below.' :
               mode === 'invite-register' && inviteRole === 'SUPERINTENDENT' ? 'Create your login to manage your assigned vessels.' :
               mode === 'invite-register' ? 'Complete your profile to accept the invitation.' :
               'Start managing your vessels with RISQ 3.2 compliance.'}
            </p>

            {inviteCompany && (
              <div className="mt-3 flex flex-col gap-1.5">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-testid="invite-badge">
                  <LinkIcon className="w-3 h-3" />
                  Invited to {inviteCompany}{inviteVessel ? ` — ${inviteVessel}` : ''}
                </div>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium w-fit ${
                  inviteRole === 'SUPERINTENDENT'
                    ? 'bg-blue-50 border border-blue-200 text-blue-700'
                    : 'bg-teal-50 border border-teal-200 text-teal-700'
                }`}>
                  Role: {inviteRole === 'SUPERINTENDENT' ? 'Superintendent' : 'Master / Vessel'}
                </div>
              </div>
            )}
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-5">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {isRegister && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="pl-10 h-11 border-slate-200 focus:border-navy-500 focus:ring-1 focus:ring-navy-500 rounded-xl text-sm bg-slate-50 focus:bg-white transition-colors"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              {mode === 'register-company' && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Company Name</label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Pacific Maritime Ltd."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="pl-10 h-11 border-slate-200 focus:border-navy-500 focus:ring-1 focus:ring-navy-500 rounded-xl text-sm bg-slate-50 focus:bg-white transition-colors"
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="captain@vessel.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11 border-slate-200 focus:border-navy-500 focus:ring-1 focus:ring-navy-500 rounded-xl text-sm bg-slate-50 focus:bg-white transition-colors"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 h-11 border-slate-200 focus:border-navy-500 focus:ring-1 focus:ring-navy-500 rounded-xl text-sm bg-slate-50 focus:bg-white transition-colors"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-navy-700 hover:bg-navy-600 text-white rounded-xl font-semibold text-sm shadow-sm transition-all"
                data-testid="submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-1.5 justify-center">
                    {[0,1,2].map(i => (
                      <span key={i} className="w-1.5 h-1.5 rounded-full bg-white animate-dot-pulse" style={{ animationDelay: `${i * 0.18}s` }} />
                    ))}
                  </span>
                ) : (
                  mode === 'login' ? 'Sign In' :
                  mode === 'invite-register' && inviteRole === 'SUPERINTENDENT' ? 'Create Account' :
                  mode === 'invite-register' ? 'Join Fleet' :
                  'Register Company'
                )}
              </Button>
            </form>
          </div>

          {/* Toggle link */}
          {!inviteToken && (
            <div className="text-center">
              {mode === 'login' ? (
                <p className="text-sm text-slate-500">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('register-company')}
                    className="text-navy-700 hover:text-navy-500 font-semibold transition-colors"
                    data-testid="toggle-register-company"
                  >
                    Register your company
                  </button>
                </p>
              ) : (
                <p className="text-sm text-slate-500">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-navy-700 hover:text-navy-500 font-semibold transition-colors"
                    data-testid="toggle-login"
                  >
                    Sign In
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
